import { forwardRef } from "react";
import { Chip, type ChipProps } from "./chip.js";

/**
 * The six leagues from the leaderboard taxonomy. Open Source bands are
 * sized by total parameter count (Tiny ≤2B, Small ≤10B, Medium ≤35B,
 * Large ≤100B, Unbounded >100B); Closed Source is a single bucket.
 *
 * See ../leaderboard/architecture.md §2 for bucket boundaries.
 */
export type League = "tiny" | "small" | "medium" | "large" | "unbounded" | "closed";

const LEAGUE_LABEL: Record<League, string> = {
  tiny: "TINY",
  small: "SMALL",
  medium: "MEDIUM",
  large: "LARGE",
  unbounded: "∞ UNBOUNDED",
  closed: "CLOSED",
};

const LEAGUE_TONE: Record<League, NonNullable<ChipProps["tone"]>> = {
  tiny: "default",
  small: "default",
  medium: "default",
  large: "default",
  unbounded: "accent",
  closed: "muted",
};

export interface LeagueBadgeProps extends Omit<ChipProps, "tone" | "children"> {
  league: League;
  /** Override the auto-mapped tone (useful for highlighting the active league). */
  tone?: ChipProps["tone"];
}

export const LeagueBadge = forwardRef<HTMLSpanElement, LeagueBadgeProps>(
  ({ league, tone, ...props }, ref) => (
    <Chip ref={ref} tone={tone ?? LEAGUE_TONE[league]} {...props}>
      {LEAGUE_LABEL[league]}
    </Chip>
  ),
);

LeagueBadge.displayName = "LeagueBadge";
